
import React, { useEffect, useState, useMemo } from 'react';
import { PostHistoryItem, ConnectorDetails, PublicationType, AccessInfo, BxUser } from '../types';
import { getPost, getConnectors, getConnectorDetails, approvePost, rejectPost } from '../services/api';
import { Loader } from './Loader';
import { 
    Calendar, CheckCircle2, XCircle, 
    AlertTriangle, Loader2, ChevronLeft, 
    Download, User, Paperclip, 
    Image as ImageIcon, Film, Settings, Info, ThumbsUp, ThumbsDown,
    Check, ArrowLeft, X, Share2, Globe, RefreshCw
} from 'lucide-react';
import { UniversalEditor } from './editor/UniversalEditor';

interface PostDetailsProps {
    postId: string;
    onBack: () => void;
    access: AccessInfo | null;
    currentUser: BxUser | null;
}

export const PostDetails: React.FC<PostDetailsProps> = ({ postId, onBack, access, currentUser }) => {
    const [post, setPost] = useState<PostHistoryItem | null>(null);
    const [connectors, setConnectors] = useState<ConnectorDetails[]>([]);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState<'approving' | 'rejecting' | 'retrying' | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    
    // Состояние для модального окна подтверждения
    const [confirmModal, setConfirmModal] = useState<'approve' | 'reject' | 'retry' | null>(null);
    // Состояние для комментария при отклонении
    const [rejectionComment, setRejectionComment] = useState("");

    const loadData = async () => {
        setLoading(true);
        try {
            const res = await getPost(postId);
            if (!res || !res.result) throw new Error("Публикация не найдена");
            setPost(res.result);

            const connRes = await getConnectors();
            const manifests: ConnectorDetails[] = [];
            const targetIds = Array.from(new Set(res.result.targets.map(t => t.socialNetworkId || t.socialAccount?.socialNetworkId).filter(Boolean) as string[]));
            
            await Promise.all(targetIds.map(async (cid) => {
                try {
                    const details = await getConnectorDetails(cid);
                    const baseInfo = connRes.result.find(c => c.id === cid);
                    manifests.push({ ...details.result, logoUrl: baseInfo?.logoUrl });
                } catch (e) { console.warn(`Manifest failed for ${cid}`, e); }
            }));

            setConnectors(manifests);
            setError(null);
        } catch (e: any) {
            setError(e.message || "Не удалось загрузить данные публикации.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { 
        if (postId) loadData(); 
    }, [postId]);

    const handleOpenReject = () => {
        setRejectionComment("");
        setConfirmModal('reject');
    };

    const handleCloseModal = () => {
        setConfirmModal(null);
        setRejectionComment("");
    };

    const executeApprove = async () => {
        setConfirmModal(null);
        if (!postId || actionLoading) return;
        
        setActionLoading('approving');
        setError(null);

        try {
            await approvePost(postId);
            setSuccessMessage("Пост успешно одобрен и поставлен в очередь!");
            setTimeout(() => onBack(), 1500);
        } catch (e: any) {
            setError("Ошибка при одобрении: " + (e.message || "Неизвестная ошибка"));
            setActionLoading(null);
        }
    };

    const executeReject = async () => {
        setConfirmModal(null);
        if (!postId || actionLoading) return;
        
        setActionLoading('rejecting');
        setError(null);

        try {
            await rejectPost(postId, rejectionComment);
            setSuccessMessage("Публикация отклонена.");
            setRejectionComment("");
            setTimeout(() => onBack(), 1500);
        } catch (e: any) {
            setError("Ошибка при отклонении: " + (e.message || "Неизвестная ошибка"));
            setActionLoading(null);
        }
    };

    const executeRetry = async () => {
        setConfirmModal(null);
        if (!postId || actionLoading) return;
        
        setActionLoading('retrying');
        setError(null);

        try {
            // Re-approving a failed post effectively retries it in the backend logic
            await approvePost(postId);
            setSuccessMessage("Публикация отправлена на повторную обработку.");
            setTimeout(() => onBack(), 1500);
        } catch (e: any) {
            setError("Ошибка при повторной отправке: " + (e.message || "Неизвестная ошибка"));
            setActionLoading(null);
        }
    };

    const normalizedBlocks = useMemo(() => {
        if (!post?.postDocument?.blocks) return [];
        return post.postDocument.blocks.map(block => {
            let normalized = { ...block };
            if (normalized.type === 'text' && typeof normalized.content === 'string') {
                normalized.content = { text: normalized.content };
            }
            if ((normalized.type === 'media' || normalized.type === 'file') && (normalized as any).items) {
                normalized.content = { 
                    files: (normalized as any).items.map((it: any) => ({ 
                        name: it.source.split('/').pop().split('?')[0] || 'file', 
                        url: it.source, 
                        type: it.type 
                    }))
                };
            }
            return normalized;
        });
    }, [post]);

    const statusInfo = useMemo(() => {
        if (!post) return null;
        if (post.status === 'sent') return { label: 'Опубликовано', color: 'bg-emerald-50 text-emerald-600 border-emerald-100', icon: CheckCircle2 };
        if (post.status === 'failed') return { label: 'Ошибка', color: 'bg-rose-50 text-rose-600 border-rose-100', icon: AlertTriangle };
        
        switch(post.moderationStatus) {
            case 'approved': return { label: 'Одобрено', color: 'bg-blue-50 text-blue-600 border-blue-100', icon: CheckCircle2 };
            case 'rejected': return { label: 'Отклонено', color: 'bg-rose-50 text-rose-600 border-rose-100', icon: XCircle };
            default: return { label: 'Ожидает проверки', color: 'bg-amber-50 text-amber-600 border-amber-100', icon: Calendar };
        }
    }, [post]);

    // Parse specific JSON error format
    const getCleanErrorMessage = (rawError?: string) => {
        if (!rawError) return null;
        try {
            // Check if it looks like the specific JSON structure provided: {"success":false,"error":"..."}
            if (rawError.trim().startsWith('{')) {
                const parsed = JSON.parse(rawError);
                return parsed.error || parsed.message || rawError;
            }
        } catch (e) {
            // ignore JSON parse error
        }
        return rawError;
    };

    if (loading) return <Loader message="Загрузка публикации..." />;
    
    if (error && !post) return (
        <div className="fixed inset-0 z-[100] bg-white flex flex-col items-center justify-center p-6 text-center">
            <AlertTriangle className="w-16 h-16 text-rose-500 mb-4" />
            <p className="text-slate-500 mb-8 font-bold">{error}</p>
            <button onClick={onBack} className="px-8 py-3 bg-blue-600 text-white rounded-xl font-black text-sm uppercase tracking-widest shadow-lg shadow-blue-200">Вернуться назад</button>
        </div>
    );

    const StatusIcon = statusInfo?.icon || Info;
    
    // Updated Logic using allowModerate flag from backend
    const canModerate = !!post?.allowModerate && post?.moderationStatus === 'pending' && !successMessage;
    
    // UPDATED: Check if ANY target failed, regardless of the global post status. Also depends on allowModerate for permission.
    const hasFailedTargets = post?.targets?.some(t => t.status === 'failed' || !!t.errorMessage);
    const canRetry = !!post?.allowModerate && hasFailedTargets && !successMessage;

    const ParticipantsBlock = (
        <div className="mb-8 flex flex-wrap items-center gap-6">
            <div className="flex -space-x-3">
                {[post?.creator, post?.assignedModerator].filter(Boolean).map((u, i) => (
                    <div key={i} className="relative group">
                        <img 
                            src={u?.photo || `https://ui-avatars.com/api/?name=${encodeURIComponent(u?.name || 'User')}`} 
                            className="w-12 h-12 rounded-full border-4 border-white shadow-sm object-cover bg-white" 
                            alt="" 
                        />
                        <div className={`absolute -bottom-1 -right-1 w-5 h-5 rounded-full border-2 border-white flex items-center justify-center ${u === post?.creator ? 'bg-blue-500' : 'bg-indigo-500'}`}>
                            {u === post?.creator ? <User className="w-2.5 h-2.5 text-white" /> : <Settings className="w-2.5 h-2.5 text-white" />}
                        </div>
                    </div>
                ))}
            </div>
            <div>
                <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1.5">Автор и модератор</div>
                <div className="flex items-center gap-2 text-xs font-bold text-slate-700">
                    <span className="truncate max-w-[120px]">{post?.creator?.name || 'Система'}</span>
                    <span className="text-slate-300">/</span>
                    <span className="text-indigo-600 truncate max-w-[120px]">{post?.assignedModerator?.name || 'Не назначен'}</span>
                </div>
            </div>
        </div>
    );

    const TargetsStatusBlock = post?.targets && post.targets.length > 0 && (
        <div className="mt-10 pt-10 border-t border-slate-200">
            <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-6 flex items-center gap-2">
                <Share2 className="w-4 h-4" /> Каналы публикации ({post.targets.length})
            </h3>
            <div className="grid gap-3">
                {post.targets.map((target, idx) => {
                    const connector = connectors.find(c => c.id === target.socialNetworkId);
                    const cleanError = getCleanErrorMessage(target.errorMessage);
                    const isFailed = target.status === 'failed' || !!target.errorMessage;
                    const isSent = target.status === 'sent';

                    return (
                        <div key={idx} className={`p-4 rounded-2xl border transition-all ${isFailed ? 'bg-rose-50/50 border-rose-100' : 'bg-white border-slate-200'}`}>
                            <div className="flex items-start justify-between gap-4">
                                <div className="flex items-center gap-4">
                                    <div className="w-10 h-10 bg-white border border-slate-100 rounded-xl flex items-center justify-center p-1.5 shadow-sm">
                                        {connector?.logoUrl ? (
                                            <img src={connector.logoUrl} className="w-full h-full object-contain" alt="" />
                                        ) : (
                                            <Globe className="w-5 h-5 text-slate-300" />
                                        )}
                                    </div>
                                    <div>
                                        <div className="text-sm font-black text-slate-800">
                                            {target.socialAccount?.credentials?.displayName || 
                                             target.socialAccount?.credentials?.meta?.botUsername || 
                                             connector?.displayName || 
                                             'Аккаунт'}
                                        </div>
                                        <div className="flex items-center gap-2 mt-0.5">
                                            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                                                ID: {target.socialAccount?.externalId || target.socialAccountId}
                                            </span>
                                            {target.publicationType && (
                                                <>
                                                    <span className="w-0.5 h-2 bg-slate-300 rounded-full"></span>
                                                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                                                        {target.publicationType === 'reel' ? 'Reels' : target.publicationType === 'story' ? 'История' : 'Пост'}
                                                    </span>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                
                                {isFailed ? (
                                    <div className="flex items-center gap-2 text-rose-600 bg-white px-3 py-1.5 rounded-lg border border-rose-100 shadow-sm">
                                        <XCircle className="w-4 h-4" />
                                        <span className="text-[10px] font-black uppercase tracking-widest">Ошибка</span>
                                    </div>
                                ) : isSent ? (
                                    <div className="flex items-center gap-2 text-emerald-600 bg-emerald-50 px-3 py-1.5 rounded-lg border border-emerald-100">
                                        <CheckCircle2 className="w-4 h-4" />
                                        <span className="text-[10px] font-black uppercase tracking-widest">Отправлено</span>
                                    </div>
                                ) : (
                                    <div className="flex items-center gap-2 text-slate-400 bg-slate-50 px-3 py-1.5 rounded-lg border border-slate-100">
                                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                        <span className="text-[10px] font-black uppercase tracking-widest">Ожидание</span>
                                    </div>
                                )}
                            </div>

                            {/* Error Details */}
                            {cleanError && (
                                <div className="mt-3 pt-3 border-t border-rose-100/50 flex items-start gap-3">
                                    <AlertTriangle className="w-4 h-4 text-rose-500 flex-shrink-0 mt-0.5" />
                                    <div className="text-xs font-bold text-rose-700 leading-relaxed break-words">
                                        {cleanError}
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );

    const AssetsBlock = (
        <div className="mt-10 pt-10 border-t border-slate-200">
            <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-6 flex items-center gap-2">
                <Paperclip className="w-4 h-4" /> Вложения ({normalizedBlocks.filter(b => b.type === 'media' || b.type === 'file').flatMap(b => b.content.files || []).length})
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {normalizedBlocks.filter(b => b.type === 'media' || b.type === 'file').flatMap(b => b.content.files || []).map((file: any, i: number) => (
                    <a key={i} href={file.url} target="_blank" rel="noopener noreferrer" className="flex items-center justify-between p-3 bg-white border border-slate-200 rounded-2xl hover:border-blue-500 transition-all group shadow-sm">
                        <div className="flex items-center gap-4 overflow-hidden">
                            <div className="w-10 h-10 bg-slate-50 rounded-xl flex items-center justify-center text-blue-500 group-hover:bg-blue-50 transition-colors">
                                {file.type === 'video' ? <Film className="w-5 h-5" /> : file.type === 'photo' ? <ImageIcon className="w-5 h-5" /> : <Paperclip className="w-5 h-5" />}
                            </div>
                            <div className="overflow-hidden">
                                <p className="text-xs font-black text-slate-700 truncate leading-tight mb-1">{file.name || 'Вложение'}</p>
                                <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">Просмотреть</p>
                            </div>
                        </div>
                        <Download className="w-5 h-5 text-slate-300 group-hover:text-blue-500 transition-colors mr-2" />
                    </a>
                ))}
            </div>
        </div>
    );

    return (
        <div className="fixed inset-0 z-[100] bg-slate-50 flex flex-col animate-in slide-in-from-right duration-300 overflow-hidden font-sans">
            
            {/* Confirmation Modal */}
            {confirmModal && (
                <div className="fixed inset-0 z-[200] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-300">
                    <div className="bg-white rounded-[2.5rem] shadow-2xl w-full max-w-sm overflow-hidden animate-in zoom-in-95 duration-300 border border-slate-200">
                        <div className="p-8 text-center">
                            <div className={`w-20 h-20 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-lg ${
                                confirmModal === 'approve' ? 'bg-emerald-50 text-emerald-600 shadow-emerald-100' : 
                                confirmModal === 'retry' ? 'bg-blue-50 text-blue-600 shadow-blue-100' :
                                'bg-rose-50 text-rose-600 shadow-rose-100'
                            }`}>
                                {confirmModal === 'approve' && <ThumbsUp className="w-10 h-10" />}
                                {confirmModal === 'retry' && <RefreshCw className="w-10 h-10" />}
                                {confirmModal === 'reject' && <AlertTriangle className="w-10 h-10" />}
                            </div>
                            <h3 className="text-xl font-black text-slate-900 mb-2 tracking-tight">
                                {confirmModal === 'approve' ? 'Вы уверены?' : confirmModal === 'retry' ? 'Повторить публикацию?' : 'Отклонить публикацию?'}
                            </h3>
                            <p className="text-slate-500 text-sm font-semibold leading-relaxed mb-6 px-2">
                                {confirmModal === 'approve' && 'Вы подтверждаете одобрение данного контента для последующей публикации.'}
                                {confirmModal === 'retry' && 'Мы попробуем отправить этот пост заново во все каналы, где произошла ошибка.'}
                                {confirmModal === 'reject' && 'Публикация будет отклонена и не попадет в очередь на отправку.'}
                            </p>

                            {/* Rejection Comment Field */}
                            {confirmModal === 'reject' && (
                                <div className="mb-6 text-left animate-in slide-in-from-bottom-2 fade-in">
                                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">Отзыв (причина)</label>
                                    <textarea 
                                        value={rejectionComment}
                                        onChange={(e) => setRejectionComment(e.target.value)}
                                        placeholder="Укажите причину отклонения (опционально)..."
                                        className="w-full p-4 bg-rose-50/50 border border-rose-100 rounded-2xl text-sm font-medium text-slate-700 outline-none focus:border-rose-300 focus:bg-white focus:ring-4 focus:ring-rose-100 transition-all resize-none h-28 placeholder:text-slate-400/80"
                                    />
                                </div>
                            )}

                            <div className="flex gap-4">
                                <button 
                                    onClick={handleCloseModal}
                                    className="flex-1 py-4 bg-slate-100 hover:bg-slate-200 text-slate-600 font-black rounded-2xl transition-all text-xs uppercase tracking-widest active:scale-95"
                                >
                                    Отмена
                                </button>
                                <button 
                                    onClick={
                                        confirmModal === 'approve' ? executeApprove : 
                                        confirmModal === 'retry' ? executeRetry :
                                        executeReject
                                    }
                                    className={`flex-1 py-4 text-white font-black rounded-2xl transition-all shadow-lg active:scale-95 text-xs uppercase tracking-widest ${
                                        confirmModal === 'approve' ? 'bg-emerald-600 hover:bg-emerald-700 shadow-emerald-200' : 
                                        confirmModal === 'retry' ? 'bg-blue-600 hover:bg-blue-700 shadow-blue-200' :
                                        'bg-rose-600 hover:bg-rose-700 shadow-rose-200'
                                    }`}
                                >
                                    {confirmModal === 'retry' ? 'Повторить' : 'Подтвердить'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* STICKY HEADER */}
            <header className="bg-white border-b border-slate-200 z-[110] flex-shrink-0 shadow-sm">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex flex-col sm:flex-row items-center justify-between gap-4">
                    
                    <div className="flex items-center gap-4 w-full sm:w-auto">
                        <button onClick={onBack} disabled={!!actionLoading} className="p-2.5 bg-slate-50 hover:bg-slate-100 rounded-xl text-slate-500 transition-all border border-slate-100 active:scale-95 disabled:opacity-50">
                            <ChevronLeft className="w-6 h-6" />
                        </button>
                        <div>
                            <div className="flex items-center gap-2">
                                <h1 className="text-sm sm:text-lg font-black text-slate-900 tracking-tight whitespace-nowrap">
                                    Публикация <span className="text-blue-600">#{postId.split('-')[0].slice(0,6)}</span>
                                </h1>
                                {statusInfo && (
                                    <div className={`flex px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest border items-center gap-1.5 ${statusInfo.color}`}>
                                        <StatusIcon className="w-3.5 h-3.5" />
                                        {statusInfo.label}
                                    </div>
                                )}
                            </div>
                            <p className="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em] mt-1">
                                {post ? new Date(post.createdAt).toLocaleString('ru-RU') : 'Загрузка...'}
                            </p>
                        </div>
                    </div>

                    <div className="flex items-center gap-3 w-full sm:w-auto justify-end">
                        {successMessage ? (
                            <div className="flex items-center gap-3 px-6 py-3 bg-emerald-50 text-emerald-700 rounded-2xl border border-emerald-100 font-black text-xs uppercase tracking-widest animate-in zoom-in-95">
                                <Check className="w-5 h-5" />
                                {successMessage}
                            </div>
                        ) : canModerate ? (
                            <>
                                <button 
                                    onClick={handleOpenReject}
                                    disabled={!!actionLoading}
                                    className="flex items-center justify-center gap-2 px-6 py-3 bg-white text-rose-600 border border-rose-200 rounded-2xl font-black text-[10px] uppercase tracking-widest hover:bg-rose-50 transition-all active:scale-95 disabled:opacity-50"
                                >
                                    {actionLoading === 'rejecting' ? <Loader2 className="w-4 h-4 animate-spin" /> : <ThumbsDown className="w-4 h-4" />}
                                    Отклонить
                                </button>
                                <button 
                                    onClick={() => setConfirmModal('approve')}
                                    disabled={!!actionLoading}
                                    className="flex items-center justify-center gap-2 px-8 py-3 bg-emerald-600 text-white rounded-2xl font-black text-[10px] uppercase tracking-widest hover:bg-emerald-700 transition-all shadow-lg shadow-emerald-600/20 active:scale-95 disabled:opacity-50"
                                >
                                    {actionLoading === 'approving' ? <Loader2 className="w-4 h-4 animate-spin" /> : <ThumbsUp className="w-4 h-4" />}
                                    Одобрить пост
                                </button>
                            </>
                        ) : canRetry ? (
                             <button 
                                onClick={() => setConfirmModal('retry')}
                                disabled={!!actionLoading}
                                className="flex items-center justify-center gap-2 px-8 py-3 bg-blue-600 text-white rounded-2xl font-black text-[10px] uppercase tracking-widest hover:bg-blue-700 transition-all shadow-lg shadow-blue-600/20 active:scale-95 disabled:opacity-50"
                            >
                                {actionLoading === 'retrying' ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                                Повторить публикацию
                            </button>
                        ) : post?.status === 'sent' && (
                            <div className="flex items-center gap-2 px-5 py-3 bg-emerald-50 text-emerald-600 rounded-2xl border border-emerald-100 font-black text-[10px] uppercase tracking-widest">
                                <CheckCircle2 className="w-4 h-4" /> Пост опубликован
                            </div>
                        )}
                    </div>
                </div>
                
                {/* Error Banner */}
                {error && (
                    <div className="bg-rose-50 border-t border-rose-100 px-6 py-3 flex items-center justify-between text-rose-700 animate-in slide-in-from-top-2">
                        <div className="flex items-center gap-3 text-xs font-bold">
                            <AlertTriangle className="w-4 h-4" />
                            {error}
                        </div>
                        <button onClick={() => setError(null)} className="p-1 hover:bg-rose-100 rounded-md">
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                )}
            </header>

            <div className="flex-1 overflow-hidden relative">
                {post && (
                    <UniversalEditor 
                        readOnly
                        manifest={connectors[0]?.manifest || ({} as any)}
                        connectors={connectors}
                        document={{ 
                            ...post.postDocument, 
                            blocks: normalizedBlocks, 
                            publicationType: (post.postDocument?.publicationType || post.targets[0]?.publicationType || 'feed') as PublicationType,
                            networkSettings: post.postDocument?.networkSettings || {} 
                        }}
                        onChange={() => {}}
                        topContent={ParticipantsBlock}
                        bottomContent={<>{TargetsStatusBlock}{AssetsBlock}</>}
                    />
                )}
            </div>
        </div>
    );
};
